import { useState } from "react";
import FinishEMRModal from "./FinishEMRModal";
import { useUpdateTempEMRAPI } from "@/app/hooks/api_hooks/emr/useUpdateTempEMRAPI";
import { useNavigate } from "react-router";
import { useNewCartAPI } from "@/app/hooks/api_hooks/Cart/useNewCartAPI";
import { useAppSelector } from "@/app_redux/hooks/root_hook";
import { EMRdetailsProps } from "@/api_framework/api_modals/EMRs";
import { useEMRDetailsAPI } from "@/app/hooks/api_hooks/emr/useEMRDetailsAPI";
import toast from "react-hot-toast";
import { BLUE7_BORIVALI_CLINIC_ID, PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { useVoiceSession } from "@/app/context/VoiceSessionContext";


const FinishPrescriptionButton = ({
  emrDetails,
  setErrorMessage,
  scrollToField,
  isBilable,
  setIsBilable,
}: EMRdetailsProps) => {
  const [showModal, setShowModal] = useState(false);
  const { execute: createNewCart } = useNewCartAPI();
  const { execute: updateEmrAPI } = useUpdateTempEMRAPI();

  const { mappedClinicId, mappedClinicName } = useAppSelector((state) => state.root.UIStates);

  const customerId = emrDetails?.customer_details?.customer_id;
  const petId = emrDetails?.pet_details?.pet_id;

  const fetchEMRDetailsAPI = useEMRDetailsAPI();
  const [loader, setLoader] = useState(false);

  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const emr_id = params.get("emr_id");

  // Submitting the EMR is the one action that ends a floating voice session.
  const { endSession: endVoiceSession } = useVoiceSession();

  const hasInvalidQuantity = (items: any[] = []) => {
    return items.some((item) => {
      const q = Number(item?.quantity);
      return !Number.isFinite(q) || q <= 0 || !Number.isInteger(q);
    });
  };

  const hasAnyInvalidQuantityInEMR = (data: any) => {
    return (
      hasInvalidQuantity(data?.lab_test_and_diagnostics?.lab_test_added) ||
      hasInvalidQuantity(data?.lab_test_and_diagnostics?.lab_test_recommended) ||
      hasInvalidQuantity(data?.treatments?.treatments_added) ||
      hasInvalidQuantity(data?.medicines?.medicines_added) ||
      hasInvalidQuantity(data?.food_and_diet?.food_added)
    );
  };

  const hasLocalInvalidQuantityInput = () => {
    if (typeof document === "undefined") return false;
    const invalidInputs = document.querySelectorAll('input[data-emr-quantity="true"]:invalid');
    return invalidInputs.length > 0;
  };

  const scrollToFirstInvalidQuantityInput = () => {
    if (typeof document === "undefined") return;

    const firstInvalidInput = document.querySelector(
      'input[data-invalid-quantity="true"], input[data-emr-quantity="true"]:invalid',
    ) as HTMLInputElement | null;

    if (firstInvalidInput) {
      firstInvalidInput.scrollIntoView({ behavior: "smooth", block: "center" });
      firstInvalidInput.focus();
    }
  };

  const hasMissingXValueInput = () => {
    if (typeof document === "undefined") return false;

    const xInputs = Array.from(document.querySelectorAll('input[name$=".x_value"]')) as HTMLInputElement[];
    const firstMissingXInput = xInputs.find((input) => !input.disabled && input.offsetParent !== null && !input.value?.trim());

    if (firstMissingXInput) {
      firstMissingXInput.scrollIntoView({ behavior: "smooth", block: "center" });
      firstMissingXInput.focus();
      toast.error("Please fill x value before finish prescription.");
      return true;
    }

    return false;
  };

  const hasMissingMandatoryMedicineInput = () => {
    if (typeof document === "undefined") return false;

    const mandatoryInputs = Array.from(
      document.querySelectorAll('input[data-medicine-required-empty="true"]'),
    ) as HTMLInputElement[];
    const firstMissingMandatoryInput = mandatoryInputs.find(
      (input) => !input.disabled && input.offsetParent !== null,
    );

    if (firstMissingMandatoryInput) {
      firstMissingMandatoryInput.scrollIntoView({ behavior: "smooth", block: "center" });
      firstMissingMandatoryInput.focus();
      toast.error("Please fill mandatory medicine fields before proceeding.");
      return true;
    }

    return false;
  };

  const hasMissingFollowUpService = (data?: any) => {
    const dataToCheck = data || emrDetails;
    const followUpService = dataToCheck?.follow_up?.follow_up_service;
    if (!followUpService || (Array.isArray(followUpService) && followUpService.length === 0)) {
      const followUpElement = document.getElementById("follow-up-section");
      if (followUpElement) {
        followUpElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      toast.error("Please select a follow-up service before finishing the EMR.");
      return true;
    }
    return false;
  };

  const hasMissingDewormingDate = (data?: any) => {
    const dataToCheck = data || emrDetails;
    if (dataToCheck?.emr_type !== "Post Grooming Consultation") return false;
    const dewormingStatus = (dataToCheck?.basic_checkup?.deworming?.status ?? "").replace(/\s+/g, "").toLowerCase();
    if (["upcoming", "recorded", "uptodate"].includes(dewormingStatus)) return false;

    const dewormingDate = dataToCheck?.basic_checkup?.deworming?.new_date;
    if (!dewormingDate || !String(dewormingDate).trim()) {
      const basicCheckupElement = document.querySelector('[data-voice-section="basic-checkup"]') as HTMLElement | null;
      if (basicCheckupElement) {
        basicCheckupElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      toast.error("Please select a deworming date before finishing the prescription.");
      return true;
    }
    return false;
  };

  const hasMissingFollowUpDate = (data?: any) => {
    const dataToCheck = data || emrDetails;
    const followUpService = dataToCheck?.follow_up?.follow_up_service;
    const followUpDate = dataToCheck?.follow_up?.follow_up_date;

    const hasService =
      followUpService &&
      (!Array.isArray(followUpService) || followUpService.length > 0);

    const isNotRequired =
      followUpService === "Not Required" ||
      (Array.isArray(followUpService) && followUpService.includes("Not Required"));

    if (hasService && !isNotRequired && (!followUpDate || !followUpDate.trim())) {
      const followUpElement = document.getElementById("follow-up-section");
      if (followUpElement) {
        followUpElement.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      toast.error("Please select a follow-up date before finishing the EMR.");
      return true;
    }
    return false;
  };

  const handleFinish = async () => {
    if (hasMissingXValueInput()) return;
    if (hasMissingMandatoryMedicineInput()) return;

    const { status, data } = await fetchEMRDetailsAPI(emr_id);
    if (!status) return;

    // const valid = isDataValid(data);

    // console.log(valid, "valid")

    // if (!valid) return;

    setLoader(true);
    const constructedBody = {
      emr_status: isBilable ? "DRAFT" : "COMPLETED",
      billables_complete: isBilable ? true : undefined,
    };
    updateEmrAPI(emr_id, constructedBody).then(({ status }) => {
      if (status) {
        if (!isBilable) {
          // Final submit (EMR marked COMPLETED): stop and dismiss the floating
          // voice recorder for this EMR.
          endVoiceSession(emr_id ?? undefined);
          navigate(`/app/customer/profile/customer-details/EMR/${customerId}/${petId}`);
        }
        setIsBilable(false);
        isBilable && toast.success("Billable item saved successfully");
      }
      setLoader(false);
    });
  };

  const isDataValid = (data: any) => {
    console.log(data, "data")
    if (hasLocalInvalidQuantityInput()) {
      toast.error("Decimal, Zero and Negative values are not allowed !");
      scrollToFirstInvalidQuantityInput();
      return false;
    }

    const missing: string[] = [];

    const fieldLabels: Record<string, string> = {
      body_condition_score: "Body Condition Score",
      dental_score: "Dental Score",
      prognosis: "Prognosis",
      general_appearance: "General Appearance",
      appetite: "Appetite",
      cmm: "CMM",
      lymph_nodes: "Lymph Nodes",
      pet_temperature: "Temperature",
      pet_weight_kg: "Weight",
    };

    let requiredFields = [];

    const petDetails = data?.pet_details;
    const weightValue = petDetails?.pet_weight_kg;
    const hasWeightValue = weightValue !== undefined && weightValue !== null && `${weightValue}` !== "";
    const weightAsNumber = Number(weightValue);
    const isWeightInvalid = hasWeightValue && (!Number.isFinite(weightAsNumber) || weightAsNumber <= 0);

    if (emrDetails?.emr_type === "Post Grooming Consultation") {
      requiredFields = [
        "body_condition_score",
        "dental_score",
        "prognosis",
        "general_appearance",
        "appetite",
        "cmm",
        "lymph_nodes",
      ];
    } else {
      requiredFields = ["prognosis"];
    }

    requiredFields.forEach((field) => {
      // validate ONLY if the key exists in the object
      if (field in data) {
        if (!data[field] && data[field] !== 0) {
          missing.push(fieldLabels[field]);
        }
      }
    });
    if (emrDetails?.emr_type === "Post Grooming Consultation") {
      // Validate only if pet_details exists and temperature key present
      if (data?.pet_details && "pet_temperature" in data.pet_details) {
        if (!data.pet_details.pet_temperature) {
          missing.push(fieldLabels["pet_temperature"]);
        }
      }

      if (petDetails && "pet_weight_kg" in petDetails) {
        if (!hasWeightValue || isWeightInvalid) {
          missing.push(fieldLabels["pet_weight_kg"]);
        }
      }
    }

    // For all EMR types, block submission if weight is explicitly provided but zero/invalid.
    if (
      petDetails &&
      "pet_weight_kg" in petDetails &&
      isWeightInvalid &&
      !missing.includes(fieldLabels["pet_weight_kg"])
    ) {
      missing.push(fieldLabels["pet_weight_kg"]);
    }
    if (hasAnyInvalidQuantityInEMR(data)) {
      toast.error("Decimal, Zero and Negative values are not allowed !");
      scrollToFirstInvalidQuantityInput();
      return false;
    }

    setErrorMessage(missing);

    if (missing.length > 0) {
      scrollToField(missing[0]);
    }

    return missing.length;
  };

  const hasMissingGroomingReason = (data?: any) => {
    const groomingVisit = (data || emrDetails)?.grooming_visit;
    if (!groomingVisit?.not_advised || groomingVisit?.reason?.trim()) return false;
    document.getElementById("grooming-visit-section")?.scrollIntoView({ behavior: "smooth", block: "center" });
    toast.error("Please enter the reason grooming is not advised before finishing the EMR.");
    return true;
  };

  const handelCheckValidation = async () => {
    if (hasMissingXValueInput()) return;
    if (hasMissingMandatoryMedicineInput()) return;

    const { status, data } = await fetchEMRDetailsAPI(emr_id);
    if (!status) return;

    const isBlue7BorivaliClinic = data?.visited_clinic_id === BLUE7_BORIVALI_CLINIC_ID;
    if (!isBlue7BorivaliClinic) {
      if (hasMissingDewormingDate(data)) return;
      if (hasMissingFollowUpService(data)) return;
      if (hasMissingFollowUpDate(data)) return;
    }
    if (hasMissingGroomingReason(data)) return;

    // const valid = isDataValid(data);

    // if (valid) {
    setShowModal(true);
    // }
  };

  const handleGoToCart = () => {
    if (hasMissingMandatoryMedicineInput()) return;

    createNewCart(customerId, mappedClinicId, mappedClinicName, petId).then(({ cart_id }) => {
      if (cart_id) {
        window.open(`/app/user-cart/cart-creation/${customerId}/${cart_id}?from_cart=true&pet_id=${petId}`, "_blank");
      }
    });
  };

  const alert_category_list = ["Dr Reminder", "hr_follow_up_check_emr"];


  return (
    <>
      {isBilable ? (
        <div className="flex justify-end gap-2">
          <button
            className="border border-[#E2E2E2] py-2 px-2  rounded-lg text-sm font-medium bg-white"
            onClick={() => setIsBilable(false)}
          >
            Cancel
          </button>
          <button
            className="border bg-[#000000] py-2 px-8  whitespace-nowrap rounded-lg text-sm font-medium text-white"
            onClick={() => handleFinish()}
          >
            Complete Billable
          </button>
        </div>
      ) : (
        <div className="flex gap-3 justify-end py-2 mt-3">
          <button
            className={SECONDARY_BUTTON}
            onClick={() => handleGoToCart()}
          >
            Go to Cart
          </button>
          <button
            className={PRIMARY_BUTTON}
            onClick={() => handelCheckValidation()}
          >
            Finish Prescription
          </button>
        </div>
      )}

      <FinishEMRModal
        isOpen={showModal}
        loader={loader}
        onClose={() => setShowModal(false)}
        onSubmit={handleFinish}
        activeEMRId={emr_id}
        alert_category_list={alert_category_list}
      />
    </>
  );
};

export default FinishPrescriptionButton;
